import { useQuery } from "@apollo/client";
import { clientNeoAuth } from "@neonumy/api-clients";
import { HOUR_FORMAT_FOR_API } from "@neonumy/neonumy-ui";
import { Form, FormInstance } from "antd5";
import useStore from "config/store";
import dayjs from "dayjs";
import useGeneralPermissions from "hooks/Permissions/useGeneralPermissions";
import { LIST_USER_CATEGORY } from "queries/neoauth/users";
import { useEffect, useState } from "react";
import { onErrorGraphql } from "utils/onErrorGraphql";
import useCollabsApi from "views/Collaborators/hooks/useCollabsApi";
import useCreateUpdateUserMutation from "views/Collaborators/hooks/useCreateUpdateUserMutation";
import useGetListGroups from "views/Collaborators/hooks/useGetListGroups";
import useGetUserQuery from "views/Collaborators/hooks/useGetUserQuery";

const STAFF_CATEGORY_SLUGS = ["newrai", "accounting-firm", "collab"];

const VALIDATE_BOOK_ENTRIES_PERMISSION = "neossi-api.can-validate-book-entries";

interface UseUserFormParams {
  userId: ID;
  form: FormInstance<CollaboratorFormValues>;
  onFinish?: (newUserId: ID) => void;
}

export function useUserForm({ userId, form, onFinish }: UseUserFormParams) {
  const isEdit = !!userId;
  const createUpdateUserLoading = useStore(state => state.createUpdateUserLoading);
  const [applicationGroups, setApplicationGroups] = useState<UserGroupType[]>([]);

  const { groups, listGroups } = useGetListGroups({ form, setApplicationGroups });
  const { workShiftChoices } = useCollabsApi();

  const { data: userCategories, error: userCategoriesError } = useQuery<ListUserCategoryOutput>(
    LIST_USER_CATEGORY,
    { client: clientNeoAuth, fetchPolicy: "cache-first" },
  );

  useEffect(() => {
    if (userCategoriesError) onErrorGraphql(userCategoriesError);
  }, [userCategoriesError]);
  const {
    fetchUser,
    getUserLoading,
    userIsClient,
    canUpdateUser,
    userCategory,
    userGroups,
    hideApplicationGroupsInput,
  } = useGetUserQuery({
    form,
    groups,
    setApplicationGroups,
  });

  const { createUpdateUser } = useCreateUpdateUserMutation({
    form,
    isEdit,
    onMutationCompleted: userId => {
      if (userId) {
        if (onFinish) onFinish(userId);
      }
    },
  });

  const { canCreateUser, isDirector } = useGeneralPermissions();
  const canAddOrUpdateUser = userId ? canUpdateUser : canCreateUser;

  const isNewraiUser = Form.useWatch("isNewraiUser", form);
  const groupSelect = Form.useWatch("applicationGroups", form);
  const selectedGroupOption = applicationGroups.find(item => item?.value === groupSelect);

  const selectedGroup = listGroups.find(group => String(group.id) === String(groupSelect));

  const showCanUseBlockedSsiAccounts =
    isDirector &&
    !!isNewraiUser &&
    !!selectedGroup?.permissions?.includes(VALIDATE_BOOK_ENTRIES_PERMISSION);

  useEffect(() => {
    if (userId && groups.length > 0) {
      fetchUser(Number(userId));
    }
  }, [userId, groups]);

  useEffect(() => {
    if (!isEdit) {
      if (selectedGroupOption?.slug === "atc") {
        form.setFieldValue("workTime", dayjs().set("hour", 7).set("minute", 0).set("second", 0));
      }
      if (["cp1", "cp2", "cp3", "cp4"].includes(selectedGroupOption?.slug ?? "")) {
        form.setFieldValue("workTime", dayjs().set("hour", 9).set("minute", 0).set("second", 0));
      }
    }
  }, [groupSelect]);

  // The backend sets `isNewraiUser` from the category, so only the category is sent.
  // Only staff move between `newrai` and `accounting-firm` (`collab` is deprecated);
  // any other category is kept.
  const resolveUserCategory = (isNewrai?: boolean): ID => {
    const categories = userCategories?.listUserCategory ?? [];
    const currentSlug = categories.find(
      category => String(category.id) === String(userCategory),
    )?.slug;
    if (isEdit && !STAFF_CATEGORY_SLUGS.includes(currentSlug ?? "")) return userCategory;

    const slug = isNewrai ? "newrai" : "accounting-firm";
    return categories.find(category => category.slug === slug)?.id;
  };

  const onFinishForm = async (values: CollaboratorFormValues) => {
    const id = userId ? { id: userId } : undefined;
    // Only a director may change it, and only once the groups have loaded: otherwise
    // the saved value is left as it is.
    const canUseBlockedSsiAccounts =
      isDirector && selectedGroup
        ? showCanUseBlockedSsiAccounts && !!values.canUseBlockedSsiAccounts
        : undefined;
    // Built field by field: the input type rejects the form's extra fields.
    createUpdateUser({
      variables: {
        input: {
          ...id,
          email: values.email,
          username: values.email ?? "",
          firstName: values.firstName,
          lastName: values.lastName,
          shortName: values.shortName,
          phone: values.phone,
          isActive: values.isActive,
          accountingFirms: values.accountingFirms,
          workShift: values.workShift,
          registrationNumber: values.registrationNumber,
          applicationGroups: values.applicationGroups
            ? [...(userGroups ?? []), values.applicationGroups]
            : userGroups,
          startedWorkingAt: values.startDate ? dayjs(values.startDate).toISOString() : undefined,
          endedWorkingAt: values.endDate ? dayjs(values.endDate).toISOString() : undefined,
          workTime: values.workTime
            ? dayjs(values.workTime).format(HOUR_FORMAT_FOR_API)
            : undefined,
          country: values.country?.value,
          userCategory: resolveUserCategory(values.isNewraiUser),
          canUseBlockedSsiAccounts,
        },
      },
    });
  };

  const onValuesChange = (changedValues: { isNewraiUser?: boolean }) => {
    if ("isNewraiUser" in changedValues) {
      const isNewraiUser = changedValues.isNewraiUser;
      const userType = isNewraiUser ? "newrai" : "standard";
      setApplicationGroups(groups.filter(item => item.userType === userType));
      form.setFieldsValue({ applicationGroups: undefined });
    }
  };

  return {
    isEdit,
    showCanUseBlockedSsiAccounts,
    applicationGroups,
    isNewraiUser,
    getUserLoading,
    createUpdateUserLoading,
    userIsClient,
    canAddOrUpdateUser,
    workShiftChoices,
    hideApplicationGroupsInput,
    onFinishForm,
    onValuesChange,
  };
}
